import React, { useEffect, useState, useCallback } from "react";
import { Container, Button, Alert, Spinner, Tabs, Tab, Form } from "react-bootstrap";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { api } from "../../../contexts/AuthContext";

import TransactionList from "./TransactionList";
import SubscriptionList from "./SubscriptionList";
import FinancialOverview from "./FinancialOverview";
import BankAccounts from "./Accounts";
import DebtsList from "./DebtsList";
import TransactionModal from "./TransactionModal";
import SubscriptionModal from "./SubscriptionModal";
import BankAccountModal from "./AccountModal";
import DebtModal from "./DebtModal";

const SIDEBAR_BLUE = "#60A5FA";

const styles = {
  tab: {
    color: "#fff",
    backgroundColor: "#1e1e1e",
    border: "1px solid #333",
  },
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [startDate, setStartDate] = useState(moment().startOf("month").toDate());
  const [endDate, setEndDate] = useState(moment().endOf("month").toDate());

  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [transactionsRes, bankAccountsRes, subscriptionsRes, debtsRes, companiesRes] =
        await Promise.all([
          api.get("/api/transactions/"),
          api.get("/api/accounts/"),
          api.get("/api/subscriptions/"),
          api.get("/api/debts/"),
          api.get("/api/companies/").catch(() => ({ data: [] })),
        ]);

      setTransactions(transactionsRes.data);
      setBankAccounts(bankAccountsRes.data);
      setSubscriptions(subscriptionsRes.data);
      setDebts(debtsRes.data);
      setCompanies(companiesRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.response?.data?.message || "Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeModals = () => {
    setShowTransactionModal(false);
    setShowSubscriptionModal(false);
    setShowBankModal(false);
    setShowDebtModal(false);
    setEditingItem(null);
    setEditingType(null);
  };

  const handleTransactionSubmit = async (transactionData) => {
    try {
      if (editingType === "transaction" && editingItem) {
        await api.put(`/api/transactions/${editingItem.id}/`, transactionData);
        toast.success("Transaction updated");
      } else {
        await api.post("/api/transactions/", transactionData);
        toast.success("Transaction logged");
      }
      closeModals();
      await fetchData();
    } catch (err) {
      console.error("Error saving transaction:", err);
      toast.error("Failed to save transaction");
    }
  };

  const handleSubscriptionSubmit = async (subscriptionData) => {
    try {
      const formattedData = {
        name: subscriptionData.name,
        description: subscriptionData.description || null,
        subscription_type: subscriptionData.subscription_type,
        category: subscriptionData.category || "personal",
        amount: Number(subscriptionData.amount),
        currency: subscriptionData.currency || "EUR",
        frequency: subscriptionData.frequency || "monthly",
        start_date: moment(subscriptionData.start_date).format("YYYY-MM-DD"),
        end_date: subscriptionData.end_date
          ? moment(subscriptionData.end_date).format("YYYY-MM-DD")
          : null,
        company: subscriptionData.company || null,
      };

      if (editingType === "subscription" && editingItem) {
        await api.put(`/api/subscriptions/${editingItem.id}/`, formattedData);
        toast.success("Subscription updated");
      } else {
        await api.post("/api/add-subscription/", formattedData);
        toast.success("Subscription created");
      }
      closeModals();
      await fetchData();
    } catch (err) {
      console.error("Failed to save subscription:", err);
      toast.error("Failed to save subscription");
    }
  };

  const handleBankAccountSubmit = async (accountData) => {
    try {
      if (editingType === "bank" && editingItem) {
        await api.put(`/api/accounts/${editingItem.id}/`, accountData);
        toast.success("Account updated");
      } else {
        await api.post("/api/accounts/", accountData);
        toast.success("Account created");
      }
      closeModals();
      await fetchData();
    } catch (err) {
      console.error("Failed to save bank account:", err);
      toast.error("Failed to save account");
    }
  };

  const handleDebtSubmit = async (debtData) => {
    try {
      if (editingType === "debt" && editingItem) {
        await api.put(`/api/debts/${editingItem.id}/`, debtData);
        toast.success("Debt updated");
      } else {
        await api.post("/api/debts/", debtData);
        toast.success("Debt added");
      }
      closeModals();
      await fetchData();
    } catch (err) {
      console.error("Failed to save debt:", err);
      toast.error("Failed to save debt");
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    setEditingType(type);
    if (type === "transaction") setShowTransactionModal(true);
    if (type === "subscription") setShowSubscriptionModal(true);
    if (type === "bank") setShowBankModal(true);
    if (type === "debt") setShowDebtModal(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    const endpoints = {
      transaction: "transactions",
      subscription: "subscriptions",
      bank: "accounts",
      debt: "debts",
    };
    try {
      await api.delete(`/api/${endpoints[type]}/${id}/`);
      toast.success("Deleted");
      await fetchData();
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err);
      toast.error("Failed to delete");
    }
  };

  const handleSettleDebt = async (id) => {
    try {
      await api.post(`/api/debts/${id}/settle/`);
      toast.success("Marked settled");
      await fetchData();
    } catch (err) {
      toast.error("Failed to settle");
    }
  };

  const handleRecordPayment = async (id, amount) => {
    try {
      await api.post(`/api/debts/${id}/record_payment/`, { amount });
      toast.success("Payment recorded");
      await fetchData();
    } catch (err) {
      toast.error("Failed to record payment");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" className="text-center">{error}</Alert>;
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 style={{ color: SIDEBAR_BLUE, marginBottom: 4 }}>Money</h2>
          <div style={{ color: "#9ca3af", fontSize: 13 }}>
            Accounts · spending · income · who owes you
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-primary"
            style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            onClick={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowTransactionModal(true);
            }}
          >
            <FaPlus className="me-2" /> Log money
          </Button>
          <Button
            variant="outline-primary"
            style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            onClick={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowDebtModal(true);
            }}
          >
            <FaPlus className="me-2" /> Add debt
          </Button>
          <Button
            variant="outline-primary"
            style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            onClick={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowSubscriptionModal(true);
            }}
          >
            <FaPlus className="me-2" /> Add subscription
          </Button>
          <Button
            variant="outline-primary"
            style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            onClick={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowBankModal(true);
            }}
          >
            <FaPlus className="me-2" /> Add account
          </Button>
        </div>
      </div>

      <style>
        {`
          .custom-tabs .nav-link {
            color: #ffffff !important;
            border: none;
            padding: 0.5rem 1rem;
            margin-right: 0.5rem;
          }
          .custom-tabs .nav-link:hover,
          .custom-tabs .nav-link.active {
            color: #60A5FA !important;
            background-color: #2d2d2d !important;
            border-bottom: 2px solid #60A5FA !important;
          }
          .custom-tabs .nav-tabs { border-bottom: 1px solid #404040; }
          .custom-pagination .page-link {
            background-color: #2d2d2d; border-color: #404040; color: #ffffff;
          }
          .custom-pagination .page-item.active .page-link {
            background-color: #60A5FA; border-color: #60A5FA;
          }
        `}
      </style>

      <div className="text-center mb-3 d-flex gap-3 justify-content-center flex-wrap">
        <div className="d-flex align-items-center">
          <Form.Label className="mb-0 me-2 text-light">From</Form.Label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control bg-dark text-light border-secondary"
            maxDate={endDate}
          />
        </div>
        <div className="d-flex align-items-center">
          <Form.Label className="mb-0 me-2 text-light">To</Form.Label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control bg-dark text-light border-secondary"
            minDate={startDate}
          />
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "overview")}
        className="mb-4 custom-tabs ath-dark-tabs"
      >
        <Tab eventKey="overview" title="Overview" tabClassName="text-light" style={styles.tab}>
          <FinancialOverview
            transactions={transactions}
            subscriptions={subscriptions}
            bankAccounts={bankAccounts}
            debts={debts}
            dateRange={{ startDate, endDate }}
          />
        </Tab>
        <Tab eventKey="transactions" title="Transactions">
          <TransactionList
            transactions={transactions}
            onEdit={(transaction) => handleEdit(transaction, "transaction")}
            onDelete={(id) => handleDelete(id, "transaction")}
            bankAccounts={bankAccounts}
          />
        </Tab>
        <Tab eventKey="debts" title="Debts">
          <DebtsList
            debts={debts}
            onAdd={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowDebtModal(true);
            }}
            onEdit={(debt) => handleEdit(debt, "debt")}
            onDelete={(id) => handleDelete(id, "debt")}
            onSettle={handleSettleDebt}
            onRecordPayment={handleRecordPayment}
          />
        </Tab>
        <Tab eventKey="accounts" title="Accounts">
          <BankAccounts
            bankAccounts={bankAccounts}
            onAdd={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowBankModal(true);
            }}
            onEdit={(account) => handleEdit(account, "bank")}
            onDelete={(id) => handleDelete(id, "bank")}
          />
        </Tab>
        <Tab eventKey="subscriptions" title="Subscriptions">
          <SubscriptionList
            subscriptions={subscriptions}
            onAdd={() => {
              setEditingItem(null);
              setEditingType(null);
              setShowSubscriptionModal(true);
            }}
            onEdit={(subscription) => handleEdit(subscription, "subscription")}
            onDelete={(id) => handleDelete(id, "subscription")}
          />
        </Tab>
      </Tabs>

      <TransactionModal
        show={showTransactionModal}
        onHide={closeModals}
        bankAccounts={bankAccounts}
        onSubmit={handleTransactionSubmit}
        initialData={editingType === "transaction" ? editingItem : null}
      />

      <SubscriptionModal
        show={showSubscriptionModal}
        onHide={closeModals}
        onSubmit={handleSubscriptionSubmit}
        initialData={editingType === "subscription" ? editingItem : null}
      />

      <BankAccountModal
        show={showBankModal}
        onHide={closeModals}
        onSubmit={handleBankAccountSubmit}
        initialData={editingType === "bank" ? editingItem : null}
      />

      <DebtModal
        show={showDebtModal}
        onHide={closeModals}
        onSubmit={handleDebtSubmit}
        initialData={editingType === "debt" ? editingItem : null}
        companies={companies}
      />
    </Container>
  );
};

export default Transactions;
